/**
 * Leviathan - User Permissions Page
 * Quasar Prime LLC
 *
 * Where an administrator sees and changes what one person can reach.
 *
 * Two things this screen must do that its predecessor did not:
 *
 *   1. Tell the truth. Permission groups used to display an "effective
 *      permissions" list that enforced nothing. Every switch here is backed by
 *      a server-side check, and any module that is NOT yet enforced is rendered
 *      as unavailable rather than as a toggle that appears to work.
 *
 *   2. Explain itself. Next to every granted permission it says where the
 *      permission came from — the role, a permission group, or a decision made
 *      about this person specifically. An administrator who can see that
 *      someone holds a permission but not why cannot audit the result, and will
 *      eventually grant something by accident.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, Check, Loader2, Lock, Minus, ShieldCheck, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { API_URL } from '@/config/api';
import { parseApiError } from '@/utils/errorHandler';
import { roleLabel } from '@/constants/userRoles';

interface CatalogAction {
  key: string;
  action: string;
  label: string;
  description?: string;
  sensitive: boolean;
}

interface CatalogModule {
  key: string;
  label: string;
  description: string;
  status: 'ENFORCED' | 'RESERVED';
  enforced: boolean;
  actions: CatalogAction[];
}

interface PermissionSource {
  granted: boolean;
  source: 'ADMIN_ROLE' | 'ROLE_DEFAULT' | 'PERMISSION_GROUP' | 'USER_OVERRIDE';
  detail: string;
}

interface UserPermissionData {
  user: { id: string; email: string; firstName: string; lastName: string; role: string };
  isAdministrator: boolean;
  permissionGroup: string | null;
  permissions: string[];
  sources: Record<string, PermissionSource>;
  overrides: { permission: string; effect: 'GRANT' | 'REVOKE'; reason: string | null }[];
}

const SOURCE_LABEL: Record<PermissionSource['source'], string> = {
  ADMIN_ROLE: 'Administrator',
  ROLE_DEFAULT: 'Role default',
  PERMISSION_GROUP: 'Permission group',
  USER_OVERRIDE: 'Set for this user',
};

const SOURCE_STYLE: Record<PermissionSource['source'], string> = {
  ADMIN_ROLE: 'bg-amber-500/20 text-amber-400 border-amber-500/50',
  ROLE_DEFAULT: 'bg-slate-600/30 text-slate-300 border-slate-600',
  PERMISSION_GROUP: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
  USER_OVERRIDE: 'bg-purple-500/20 text-purple-300 border-purple-500/50',
};

export default function UserPermissions() {
  const { id } = useParams<{ id: string }>();
  const { accessToken } = useAuth();

  const [catalog, setCatalog] = useState<CatalogModule[]>([]);
  const [data, setData] = useState<UserPermissionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const headers = useCallback(
    (json = false) => ({
      Authorization: `Bearer ${accessToken}`,
      ...(json ? { 'Content-Type': 'application/json' } : {}),
    }),
    [accessToken]
  );

  const load = useCallback(async () => {
    try {
      const [catalogRes, userRes] = await Promise.all([
        fetch(`${API_URL}/api/users/permissions/catalog`, { headers: headers() }),
        fetch(`${API_URL}/api/users/${id}/permissions`, { headers: headers() }),
      ]);

      if (!catalogRes.ok) throw new Error(await parseApiError(catalogRes));
      if (!userRes.ok) throw new Error(await parseApiError(userRes));

      setCatalog((await catalogRes.json()).modules);
      setData(await userRes.json());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  }, [headers, id]);

  useEffect(() => {
    load();
  }, [load]);

  const setOverride = async (permission: string, effect: 'GRANT' | 'REVOKE' | null) => {
    setSaving(permission);
    try {
      const response = await fetch(`${API_URL}/api/users/${id}/permissions`, {
        method: 'PUT',
        headers: headers(true),
        body: JSON.stringify({ permission, effect }),
      });
      if (!response.ok) throw new Error(await parseApiError(response));
      await load();
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update permission');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6">
        <div className="bg-red-500/10 border border-red-500/50 p-4 text-red-300">
          {error ?? 'User not found'}
        </div>
        <Link to="/admin/users" className="text-amber-500 hover:underline mt-4 inline-block">
          &larr; Back to Users
        </Link>
      </div>
    );
  }

  const held = new Set(data.permissions);
  const overrideFor = (key: string) => data.overrides.find((o) => o.permission === key);

  return (
    <div className="p-6 space-y-6">
      <div>
        <Link to={`/admin/users/${id}`} className="text-amber-500 hover:underline text-sm">
          &larr; Back to {data.user.firstName} {data.user.lastName}
        </Link>
        <h1 className="text-2xl font-bold text-slate-100 mt-2">Permissions</h1>
        <p className="text-slate-400 text-sm mt-1">
          {data.user.email} &middot; Access level:{' '}
          <span className="text-slate-200">{roleLabel(data.user.role)}</span>
          {data.permissionGroup && (
            <>
              {' '}
              &middot; Group: <span className="text-slate-200">{data.permissionGroup}</span>
            </>
          )}
        </p>
      </div>

      {error && (
        <div className="flex items-start justify-between bg-red-500/10 border border-red-500/50 p-4 text-red-300 text-sm">
          <span>{error}</span>
          <button onClick={() => setError(null)}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* An administrator cannot be restricted, and the screen says so instead of
          showing switches that would be stored and then disregarded. */}
      {data.isAdministrator && (
        <div className="flex items-start space-x-3 bg-amber-500/10 border border-amber-500/50 p-4">
          <ShieldCheck className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-slate-300">
            <p className="font-medium text-amber-400 mb-1">
              {roleLabel(data.user.role)} holds every permission
            </p>
            <p>
              This cannot be changed here, and it is deliberate: it is what guarantees an instance
              always has someone able to administer it. To restrict this person, change their access
              level first, on the Edit User screen.
            </p>
          </div>
        </div>
      )}

      <div className="flex items-start space-x-3 bg-slate-800 border border-slate-700 p-4 text-sm text-slate-400">
        <Lock className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
        <p>
          Permissions are enforced by the server on every request, not by hiding buttons. Someone
          without a permission is refused even if they type the address directly. Changes take
          effect immediately &mdash; the person does not need to sign out.
        </p>
      </div>

      <div className="space-y-4">
        {catalog.map((module) => {
          const unavailable = !module.enforced;

          return (
            <div
              key={module.key}
              className={`border ${
                unavailable ? 'border-slate-800 bg-slate-900/50' : 'border-slate-700 bg-slate-800'
              }`}
            >
              <div className="px-4 py-3 border-b border-slate-700 flex items-start justify-between">
                <div>
                  <h2
                    className={`font-semibold ${unavailable ? 'text-slate-500' : 'text-slate-100'}`}
                  >
                    {module.label}
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">{module.description}</p>
                </div>

                {/* RESERVED modules render as unavailable rather than as a
                    switch that appears to work. A control claiming a
                    restriction it does not apply is the exact bug this page
                    was rebuilt to remove; it must not reappear here. */}
                {unavailable && (
                  <span className="flex items-center space-x-1.5 px-2 py-1 text-[11px] border border-slate-700 text-slate-500 whitespace-nowrap">
                    <AlertTriangle className="w-3 h-3" />
                    <span>Not available yet</span>
                  </span>
                )}
              </div>

              <div className="divide-y divide-slate-700/50">
                {module.actions.map((action) => {
                  const isHeld = held.has(action.key);
                  const source = data.sources[action.key];
                  const override = overrideFor(action.key);
                  const busy = saving === action.key;
                  const locked = unavailable || data.isAdministrator;

                  return (
                    <div
                      key={action.key}
                      className="px-4 py-3 flex items-center justify-between gap-4"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-sm ${
                              unavailable
                                ? 'text-slate-600'
                                : isHeld
                                  ? 'text-slate-100'
                                  : 'text-slate-500'
                            }`}
                          >
                            {action.label}
                          </span>
                          {action.sensitive && (
                            <span className="px-1.5 py-0.5 text-[10px] border border-amber-500/40 text-amber-500/80">
                              SENSITIVE
                            </span>
                          )}
                        </div>
                        {action.description && (
                          <p className="text-xs text-slate-500 mt-0.5">{action.description}</p>
                        )}
                        {/* The "why", which is the point of this screen. */}
                        {source && !unavailable && (
                          <p className="text-xs text-slate-500 mt-1" title={source.detail}>
                            <span
                              className={`inline-block px-1.5 py-0.5 border text-[10px] mr-2 ${SOURCE_STYLE[source.source]}`}
                            >
                              {SOURCE_LABEL[source.source]}
                            </span>
                            {source.detail}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        {busy ? (
                          <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />
                        ) : (
                          <>
                            <ToggleButton
                              active={override?.effect === 'GRANT'}
                              disabled={locked}
                              tone="grant"
                              title="Grant this to this user specifically"
                              onClick={() =>
                                setOverride(
                                  action.key,
                                  override?.effect === 'GRANT' ? null : 'GRANT'
                                )
                              }
                            >
                              <Check className="w-3.5 h-3.5" />
                            </ToggleButton>
                            <ToggleButton
                              active={override?.effect === 'REVOKE'}
                              disabled={locked}
                              tone="revoke"
                              title="Deny this to this user specifically, even though the role allows it"
                              onClick={() =>
                                setOverride(
                                  action.key,
                                  override?.effect === 'REVOKE' ? null : 'REVOKE'
                                )
                              }
                            >
                              <Minus className="w-3.5 h-3.5" />
                            </ToggleButton>
                            <span
                              className={`w-16 text-center text-[11px] px-2 py-1 border ${
                                unavailable
                                  ? 'border-slate-800 text-slate-600'
                                  : isHeld
                                    ? 'border-green-500/50 text-green-400 bg-green-500/10'
                                    : 'border-slate-600 text-slate-500'
                              }`}
                            >
                              {unavailable ? 'n/a' : isHeld ? 'Allowed' : 'Denied'}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ToggleButton({
  children,
  active,
  disabled,
  tone,
  title,
  onClick,
}: {
  children: React.ReactNode;
  active: boolean;
  disabled?: boolean;
  tone: 'grant' | 'revoke';
  title: string;
  onClick: () => void;
}) {
  const activeStyle =
    tone === 'grant'
      ? 'bg-green-600 border-green-600 text-white'
      : 'bg-red-600 border-red-600 text-white';
  const idleStyle = 'border-slate-600 text-slate-500 hover:border-slate-400 hover:text-slate-300';

  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={`p-1.5 border transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
        active ? activeStyle : idleStyle
      }`}
    >
      {children}
    </button>
  );
}
